import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from './formatters';

export const generateReportPDF = (reportData, userData) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  let yPosition = 20;
  const lineHeight = 7;
  const leftMargin = 20;
  const pageWidth = 210;
  const contentWidth = pageWidth - (leftMargin * 2);

  // Configurar fuente
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  
  // Título
  doc.text(userData?.pharmacyName || 'NomoFarma', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight * 1.5;
  
  doc.setFontSize(14);
  doc.text('Reporte de Cierre de Caja', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight * 1.5;
  
  // Período
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const periodText = getPeriodText(reportData);
  doc.text(periodText, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight * 2;
  
  // Resumen General
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('RESUMEN GENERAL', leftMargin, yPosition);
  yPosition += lineHeight * 1.5;
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  
  // Datos del resumen
  const summaryData = [
    ['Ventas Totales:', `${reportData.totalSales} transacciones`],
    ['Ingresos Totales:', formatCurrency(reportData.totalRevenue)],
    ['IVA Recaudado:', formatCurrency(reportData.totalTax)],
    ['Ganancia Estimada:', formatCurrency(reportData.totalProfit)],
    ['Gastos Totales:', formatCurrency(reportData.totalExpenses)],
    ['Ganancia Neta:', formatCurrency(reportData.netProfit)]
  ];
  
  summaryData.forEach(([label, value]) => {
    doc.text(label, leftMargin, yPosition);
    doc.text(value, pageWidth - leftMargin, yPosition, { align: 'right' });
    yPosition += lineHeight;
  });
  
  yPosition += lineHeight;
  
  // Productos más vendidos
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('PRODUCTOS MÁS VENDIDOS', leftMargin, yPosition);
  yPosition += lineHeight * 1.5;
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  
  // Encabezados de tabla
  doc.setFont(undefined, 'bold');
  doc.text('Producto', leftMargin, yPosition);
  doc.text('Cantidad', pageWidth / 2, yPosition, { align: 'center' });
  doc.text('Ingresos', pageWidth - leftMargin, yPosition, { align: 'right' });
  yPosition += lineHeight;
  
  // Línea divisoria
  doc.line(leftMargin, yPosition - 2, pageWidth - leftMargin, yPosition - 2);
  
  doc.setFont(undefined, 'normal');
  
  // Productos
  reportData.topProducts.forEach((product) => {
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }
    
    // Manejar nombres largos
    const maxProductNameWidth = 80;
    const lines = doc.splitTextToSize(product.name, maxProductNameWidth);
    
    lines.forEach((line, index) => {
      if (index === 0) {
        doc.text(line, leftMargin, yPosition);
        doc.text(product.quantity.toString(), pageWidth / 2, yPosition, { align: 'center' });
        doc.text(formatCurrency(product.revenue), pageWidth - leftMargin, yPosition, { align: 'right' });
      } else {
        yPosition += lineHeight;
        doc.text(line, leftMargin, yPosition);
      }
    });
    
    yPosition += lineHeight;
  });
  
  // Detalle de ventas (nueva página)
  doc.addPage();
  yPosition = 20;
  
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('DETALLE DE VENTAS', leftMargin, yPosition);
  yPosition += lineHeight * 1.5;
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  
  // Encabezados
  doc.setFont(undefined, 'bold');
  doc.text('Fecha/Hora', leftMargin, yPosition);
  doc.text('Ticket', leftMargin + 50, yPosition);
  doc.text('Cliente', leftMargin + 80, yPosition);
  doc.text('Total', pageWidth - leftMargin, yPosition, { align: 'right' });
  yPosition += lineHeight;
  
  doc.line(leftMargin, yPosition - 2, pageWidth - leftMargin, yPosition - 2);
  
  doc.setFont(undefined, 'normal');
  
  // Ventas
  reportData.sales.forEach((sale) => {
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }
    
    const saleDate = sale.createdAt ? new Date(sale.createdAt) : new Date(sale.date);
    doc.text(format(saleDate, 'dd/MM HH:mm'), leftMargin, yPosition);
    doc.text(`#${sale.saleNumber || sale.id.slice(-8)}`, leftMargin + 50, yPosition);
    
    // Nombre del cliente (truncar si es muy largo)
    const customerName = sale.customerName || 'Público General';
    const maxCustomerWidth = 50;
    const truncatedCustomer = customerName.length > 20 ? customerName.substring(0, 20) + '...' : customerName;
    doc.text(truncatedCustomer, leftMargin + 80, yPosition);
    
    doc.text(formatCurrency(sale.total), pageWidth - leftMargin, yPosition, { align: 'right' });
    yPosition += lineHeight;
  });
  
  // Pie de página
  yPosition = 280;
  doc.setFontSize(8);
  doc.text(`Reporte generado el ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}`, pageWidth / 2, yPosition, { align: 'center' });
  
  return doc;
};

export const generateReportExcel = (reportData, userData) => {
  const wb = XLSX.utils.book_new();
  
  // Hoja de Resumen
  const summaryData = [
    ['REPORTE DE CIERRE DE CAJA'],
    [userData?.pharmacyName || 'NomoFarma'],
    [getPeriodText(reportData)],
    [],
    ['RESUMEN GENERAL'],
    ['Concepto', 'Valor'],
    ['Ventas Totales', `${reportData.totalSales} transacciones`],
    ['Ingresos Totales', formatCurrency(reportData.totalRevenue)],
    ['IVA Recaudado', formatCurrency(reportData.totalTax)],
    ['Ganancia Estimada', formatCurrency(reportData.totalProfit)],
    ['Gastos Totales', formatCurrency(reportData.totalExpenses)],
    ['Ganancia Neta', formatCurrency(reportData.netProfit)]
  ];
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  
  // Aplicar estilos básicos (ancho de columnas)
  summarySheet['!cols'] = [
    { wch: 30 },
    { wch: 20 }
  ];
  
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Resumen');
  
  // Hoja de Productos Más Vendidos
  const productsData = [
    ['PRODUCTOS MÁS VENDIDOS'],
    [],
    ['Producto', 'Cantidad', 'Ingresos']
  ];
  
  reportData.topProducts.forEach(product => {
    productsData.push([
      product.name,
      product.quantity,
      formatCurrency(product.revenue)
    ]);
  });
  
  const productsSheet = XLSX.utils.aoa_to_sheet(productsData);
  productsSheet['!cols'] = [
    { wch: 40 },
    { wch: 15 },
    { wch: 20 }
  ];
  
  XLSX.utils.book_append_sheet(wb, productsSheet, 'Productos');
  
  // Hoja de Detalle de Ventas
  const salesData = [
    ['DETALLE DE VENTAS'],
    [],
    ['Fecha/Hora', 'Ticket', 'Cliente', 'Productos', 'Subtotal', 'IVA', 'Total']
  ];
  
  reportData.sales.forEach(sale => {
    const saleDate = sale.createdAt ? new Date(sale.createdAt) : new Date(sale.date);
    salesData.push([
      format(saleDate, 'dd/MM/yyyy HH:mm', { locale: es }),
      `#${sale.saleNumber || sale.id.slice(-8)}`,
      sale.customerName || 'Público General',
      sale.items.length,
      formatCurrency(sale.subtotal),
      formatCurrency(sale.tax),
      formatCurrency(sale.total)
    ]);
  });
  
  const salesSheet = XLSX.utils.aoa_to_sheet(salesData);
  salesSheet['!cols'] = [
    { wch: 20 },
    { wch: 15 },
    { wch: 30 },
    { wch: 12 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 }
  ];
  
  XLSX.utils.book_append_sheet(wb, salesSheet, 'Ventas');
  
  // Hoja de Gastos
  if (reportData.expenses && reportData.expenses.length > 0) {
    const expensesData = [
      ['DETALLE DE GASTOS'],
      [],
      ['Fecha', 'Descripción', 'Categoría', 'Monto']
    ];
    
    reportData.expenses.forEach(expense => {
      const expenseDate = expense.date ? new Date(expense.date) : new Date(expense.createdAt);
      expensesData.push([
        format(expenseDate, 'dd/MM/yyyy', { locale: es }),
        expense.description,
        expense.category || '-',
        formatCurrency(expense.amount)
      ]);
    });
    
    const expensesSheet = XLSX.utils.aoa_to_sheet(expensesData);
    expensesSheet['!cols'] = [
      { wch: 15 },
      { wch: 40 },
      { wch: 20 },
      { wch: 15 }
    ];
    
    XLSX.utils.book_append_sheet(wb, expensesSheet, 'Gastos');
  }
  
  return wb;
};

const getPeriodText = (reportData) => {
  if (!reportData || !reportData.dateRange) return '';
  
  const { start, end } = reportData.dateRange;
  
  switch (reportData.period) {
    case 'daily':
      return format(start, "dd 'de' MMMM 'de' yyyy", { locale: es });
    case 'weekly':
      return `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`;
    case 'monthly':
      return format(start, "MMMM 'de' yyyy", { locale: es });
    default:
      return '';
  }
};

export const downloadReportPDF = (reportData, userData) => {
  const doc = generateReportPDF(reportData, userData);
  const fileName = `Reporte_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`;
  doc.save(fileName);
};

export const downloadReportExcel = (reportData, userData) => {
  const wb = generateReportExcel(reportData, userData);
  const fileName = `Reporte_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
  XLSX.writeFile(wb, fileName);
};